import { watch, ref, onUnmounted, onMounted, onBeforeMount, type ComputedRef, type HTMLAttributes, computed } from 'vue'
import { setCSSVariables, setStyles } from '../common/utils';
import { useLayoutStore } from '../store/layout';
import { storeToRefs } from 'pinia';
import { useThemeStore, type ThemeVariables } from '../store/theme';
import type { ElementLayout } from '@/types/ElementLayout';

const DEFAULT_INITIAL_FRAME_STYLES = {
  background: 'transparent!important',
  position: 'absolute!important',
  border: 'none!important',
  pointerEvents: 'all!important',
}


// the iframe manager is responsible for managing the layout of the iframe
// it sets the correct styles and applies the theme to the iframe
// it also manages the fullscreen mode of the iframe

export function useIframeManager(iframe: HTMLIFrameElement, layout: ComputedRef<ElementLayout>, options = { fullscreenable: false },
  attributes: ComputedRef<HTMLAttributes> = computed(() => ({} as HTMLAttributes))
) {
  const layoutStore = useLayoutStore();
  const theme = useThemeStore();
  const { variables: themeVariables } = storeToRefs(theme);
  const { isFullscreen } = storeToRefs(layoutStore);

  const isFullscreenable = ref(options.fullscreenable || false);
  const innerBody = iframe?.contentDocument?.body;


  onBeforeMount(() => {
    applyTheme(themeVariables.value);
  })

  onUnmounted(() => {
    // make sure the iframe is not visible anymore when unmounted
    const styles: Partial<CSSStyleDeclaration> = {};
    styles.width = '1px!important';
    styles.height = '1px!important';
    styles.bottom = '0px!important';
    styles.pointerEvents = 'none!important';
    setStyles(iframe, styles);
  });

  onMounted(() => {
    calculateIframeStyles();
    applyTheme(themeVariables.value);
    calcAttributes();
  })

  const calculateIframeStyles = () => {
    const styles: Partial<CSSStyleDeclaration> = Object.assign({}, DEFAULT_INITIAL_FRAME_STYLES);

    styles.width = layout.value.width;
    styles.height = layout.value.height;

    styles.bottom = layout.value.y;
    styles.zIndex = layout.value.zIndex.toString();

    if (layout.value.xAlignment === 'left') {
      styles.left = layout.value.x;
      styles.right = 'unset';
    } else {
      styles.right = layout.value.x;
      styles.left = 'unset';
    }

    if (isFullscreenable.value && isFullscreen.value) {
      styles.width = '100vw!important';
      styles.height = '100dvh!important';
    }

    if (!layout.value.isElementVisible) {
      styles.width = '1px!important';
      styles.height = '1px!important';
      styles.bottom = '0px!important';
      styles.pointerEvents = 'none!important';
    }

    setStyles(iframe, styles);

  }

  const calcAttributes = () => {
    for (const [key, value] of Object.entries(attributes.value)) {
      console.log(`Setting attribute ${key} to ${value}`);
      iframe.setAttribute(key, value.toString());
    }
  }

  watch(attributes, () => {
    calcAttributes();
  }, { deep: true })

  watch([layout, isFullscreenable],
    () => {
      calculateIframeStyles();
    }, { deep: true })

  function applyTheme(values: ThemeVariables) {
    if (!innerBody) return;
    setCSSVariables(innerBody.style, values);
  }
  watch(themeVariables, (newvalues) => {
    applyTheme(newvalues);
  }, { deep: true })


  function setIsFullscreenable(value: boolean) {
    isFullscreenable.value = value;
  }

  return {
    applyTheme,
    setIsFullscreenable
  }
}
